/**
 * "Export to Excel" in Matrix view — Customer review 11 Sep (item 18).
 *
 * The button exported the TABLE whichever view was on screen. In Matrix view it
 * now writes the matrix as displayed: Supplier, Supplier Code, Customer (the
 * sites the row covers), one column per month in the grid's own (fiscal) order
 * holding the grade label shown in that cell, and the Overall Rating. The Table
 * view's export is unchanged.
 *
 * CR-7297: the page has ONE Export control (usePerformanceExport) and this is
 * what it writes in Matrix view. The ticket's column list includes Supplier
 * Code, which the first cut left out.
 */
import * as XLSX from 'xlsx';
import { MatrixRatingBand, PerformanceMatrixRow } from '../../../lib/api';

/** The sheet's columns before and after the twelve months (CR-7297 AC1). */
const LEAD_COLUMNS = ['Supplier', 'Supplier Code', 'Customer'] as const;
const TRAIL_COLUMN = 'Overall Rating';

/**
 * The label a cell shows for `grade` — the band's configured label ('' for a
 * month with no grade).
 *
 * @param grade - the grade the backend graded the cell with
 * @param bands - the scale served with the rows
 */
export function matrixGradeLabel(grade: string | undefined, bands: MatrixRatingBand[]): string {
  if (!grade || grade === '-') return '';
  return bands.find((b) => b.grade === grade)?.label || grade;
}

/**
 * `Supplier_Performance_Matrix_FY2026_<date>.xlsx` in fiscal mode,
 * `…_Matrix_2026_<date>.xlsx` in calendar mode.
 *
 * CR-7297: same prefix and date stamp as the Table export
 * (`Supplier_Performance_<date>.xlsx`) so the two files from the one button
 * sort together, with the view and year in the name so neither can be
 * mistaken for the other.
 *
 * @param year - the matrix's (start) year
 * @param yearLabel - the backend's label, e.g. "FY2026 (Apr 2026 → Mar 2027)"
 * @param today - the export date (defaults to now; injectable for tests)
 */
export function matrixExportFilename(year: number, yearLabel: string, today: Date = new Date()): string {
  const fy = yearLabel.startsWith('FY') ? `FY${year}` : String(year);
  const date = today.toISOString().split('T')[0];
  return `Supplier_Performance_Matrix_${fy}_${date}.xlsx`;
}

/**
 * The Supplier Code and Customer cells for one row.
 *
 * On screen a one-site row shows the site and its code; a several-site row
 * shows "All sites (N)" with names and codes on hover. A file has no hover, so
 * it names every site, and lists each site's code in the same order ('-' where
 * a site has none, so the two lists stay aligned).
 *
 * @param row - one matrix row
 */
export function matrixRowScope(row: PerformanceMatrixRow): { code: string; customer: string } {
  const sites = row.customer_sites ?? [];
  if (sites.length === 0) return { code: row.SupplierCode || '', customer: row.Business || '' };
  return {
    code: sites.length === 1
      ? (sites[0].supplier_code || row.SupplierCode || '')
      : sites.map((s) => s.supplier_code || '-').join('; '),
    customer: sites.map((s) => s.name).join('; '),
  };
}

/**
 * The sheet rows for the matrix on screen, header order as `matrixHeaders`.
 *
 * @param rows - the rows on screen (after the band filter and quick search)
 * @param monthKeys - the grid's month order
 * @param bands - the scale served with the rows
 */
export function matrixSheetRows(
  rows: PerformanceMatrixRow[],
  monthKeys: readonly string[],
  bands: MatrixRatingBand[],
): Array<Record<string, string>> {
  return rows.map((row) => {
    const { code, customer } = matrixRowScope(row);
    const out: Record<string, string> = {
      Supplier: row.Supplier,
      'Supplier Code': code,
      Customer: customer,
    };
    monthKeys.forEach((m) => {
      out[m] = matrixGradeLabel(row[m as keyof PerformanceMatrixRow] as string, bands);
    });
    out[TRAIL_COLUMN] = matrixGradeLabel(row.OverallRating, bands);
    return out;
  });
}

/**
 * The sheet's header row.
 *
 * @param monthKeys - the grid's month order
 */
export function matrixHeaders(monthKeys: readonly string[]): string[] {
  return [...LEAD_COLUMNS, ...monthKeys, TRAIL_COLUMN];
}

/**
 * Write the matrix rows on screen to an .xlsx download.
 *
 * @returns the number of rows written (0 → nothing was downloaded)
 */
export function exportMatrix(
  rows: PerformanceMatrixRow[],
  monthKeys: readonly string[],
  bands: MatrixRatingBand[],
  year: number,
  yearLabel: string,
): number {
  if (rows.length === 0) return 0;
  const worksheet = XLSX.utils.json_to_sheet(matrixSheetRows(rows, monthKeys, bands), {
    header: matrixHeaders(monthKeys),
  });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Performance Matrix');
  XLSX.writeFile(workbook, matrixExportFilename(year, yearLabel));
  return rows.length;
}
